import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { saveUserFilter, getUserFilters, deleteUserFilter, setDefaultFilter, unsetDefaultFilter, getDefaultFilter } from '../services/userDataService'
import FilterPanel from './FilterPanel'
import HiddenBetsModal, { useHiddenBets } from './HiddenBetsModal'
import TrackBetModal, { useTrackBet } from './TrackBetModal'
import PropEvMultiBuilder from './PropEvMultiBuilder'
import { settlementFields } from '../TRACKER_PAGE/settlement'
import { buildMarketExcluder, DEFAULT_FILTERS, MARKET_AVG_TIP } from './propEvConfig'
import ShareModal from '../EV_NEW_PAGE/ShareModal'
import PreviewWall from '../components/PreviewWall'
import PropEvCard from './PropEvCard'
import PropEvTour from './propEvTour'
import '../styles/app.css'
import '../styles/app-mobile.css'
import useVisiblePolling from '../hooks/useVisiblePolling'

// ─── Constants ───────────────────────────────────────────────────────────────

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getHoursUntilGame = (ts) => {
  const diff = new Date(ts + 'Z') - new Date()
  return Math.max(0, diff / (1000 * 60 * 60))
}

const formatTimeWithSeconds = (ts) => {
  if (!ts) return ''
  return new Date(ts + 'Z').toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  })
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function PropEvMobile({ isVisible = true, isPreview = false, onOpenInfo }) {
  const { user, oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  isPreview = isPreview || authLoading || !hasAccess('prop_ev')

  const [opportunities,         setOpportunities]         = useState([])
  const [filteredOpportunities, setFilteredOpportunities] = useState([])
  const [loading,               setLoading]               = useState(true)
  const [error,                 setError]                 = useState(null)
  const [autoRefreshEnabled,    setAutoRefreshEnabled]    = useState(false)
  const [shareBet,              setShareBet]              = useState(null)
  const { hiddenBets, hideOpp, unhideOpp, clearAll, isHidden, showModal, setShowModal } = useHiddenBets()
  const [showFilterPanel,       setShowFilterPanel]       = useState(false)
  const [expandedCard,          setExpandedCard]          = useState(null)
  const [showAvgInfo,           setShowAvgInfo]           = useState(false)
  const [noteDismissed,         setNoteDismissed]         = useState(() => localStorage.getItem('propEvNoteDismissed') === '1')
  const dismissNote = () => { localStorage.setItem('propEvNoteDismissed', '1'); setNoteDismissed(true) }
  const [sortBy,                setSortBy]                = useState('ev_percentage')
  const [sortDir,               setSortDir]               = useState('desc')
  const [currentPage,           setCurrentPage]           = useState(1)
  const itemsPerPage = 25

  const [filters,       setFilters]       = useState(DEFAULT_FILTERS)
  const [kellyFraction, setKellyFraction] = useState('1/4')
  const [bankroll,      setBankroll]      = useState(1000)
  const [roundStakes,   setRoundStakes]   = useState('5')

  const { isTracked, openTrackModal, closeTrackModal, saveTrack, showTrackModal, trackingOpp, trackForm, setTrackForm } =
    useTrackBet({ kellyFraction, bankroll, roundStakes, hideOpp })

  const [savedFilters,        setSavedFilters]        = useState([])
  const [activeFilterId,      setActiveFilterId]      = useState(null)
  const [filterName,          setFilterName]          = useState('')
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)

  // Multi builder
  const [showMultiModal, setShowMultiModal] = useState(false)
  const [multiLegs,      setMultiLegs]      = useState([])
  const [pickerState,    setPickerState]    = useState(null)
  const [multiError,     setMultiError]     = useState(null)

  // ─── Derived ───────────────────────────────────────────────────────────────

  const groupedOpportunities = useMemo(() => {
    const groups = new Map()
    filteredOpportunities.forEach(opp => {
      const key = `${opp.selection}|${opp.market_type}|${opp.commence_time}|${opp.original_odds}`
      if (groups.has(key)) {
        groups.get(key).bookmakers.push({ name: opp.bookmaker, game_url: opp.game_url })
      } else {
        groups.set(key, { ...opp, bookmakers: [{ name: opp.bookmaker, game_url: opp.game_url }] })
      }
    })
    return [...groups.values()]
  }, [filteredOpportunities])

  // ─── Stake ─────────────────────────────────────────────────────────────────

  const computeStakeAmount = (kellyStake) => {
    if (typeof kellyStake !== 'number') return null
    const fractions = { '1/4': 0.25, '1/2': 0.5, '3/4': 0.75, 'Full': 1 }
    const frac = fractions[kellyFraction] ?? 0.5
    const stake = (kellyStake / 100) * frac * bankroll
    if (roundStakes === '1') return Math.round(stake)
    if (roundStakes === '5') return Math.round(stake / 5) * 5
    return Math.round(stake * 100) / 100
  }

  const computeStake = (kellyStake) => {
    const amt = computeStakeAmount(kellyStake)
    return amt == null ? '' : `$${amt}`
  }

  const evBadgeClass = (opp) => {
    const ev = opp.ev_percentage
    if (ev >= 10) return 'bc-ev-badge bc-ev-high'
    if (ev >= 5)  return 'bc-ev-badge bc-ev-mid'
    return 'bc-ev-badge bc-ev-low'
  }

  // ─── Multi Builder helpers ────────────────────────────────────────────────

  const multiLegKey = (opp) =>
    `${opp.selection}|${opp.market_type}|${opp.commence_time}|${opp.original_odds}`

  const isInMulti  = (opp) => multiLegs.some(l => l._key === multiLegKey(opp))
  const lockedBook = multiLegs.length > 0 ? multiLegs[0].bookmaker : null

  const addMultiLeg = (opp, bookmakerName) => {
    const bm = opp.bookmakers.find(b => b.name === bookmakerName)
    setMultiLegs(prev => [...prev, {
      _key:          multiLegKey(opp),
      selection:     opp.selection,
      match:         opp.match,
      market_type:   opp.market_type,
      sport:         opp.sport || 'AFL',
      commence_time: opp.commence_time,
      bookmaker:     bookmakerName,
      game_url:      bm?.game_url || null,
      odds:          opp.original_odds,
      ev_percentage: opp.ev_percentage,
      fair_odds:     opp.fair_odds,
      // Teams and the raw values behind the selection, so a multi's legs can be
      // settled against results without parsing the display string back apart.
      ...settlementFields(opp),
    }])
  }

  const handleMultiLegClick = (opp) => {
    const key = multiLegKey(opp)

    if (isInMulti(opp)) {
      setMultiLegs(prev => prev.filter(l => l._key !== key))
      return
    }

    const oppPlayer   = opp.selection.match(/^(.+?)\s+(over|under)\s+/i)?.[1]?.trim() || ''
    const oppBaseStat = opp.market_type.replace('_milestones', '')
    const correlated  = oppPlayer && multiLegs.some(l => {
      const lPlayer   = l.selection.match(/^(.+?)\s+(over|under)\s+/i)?.[1]?.trim() || ''
      const lBaseStat = l.market_type.replace('_milestones', '')
      return lPlayer === oppPlayer && lBaseStat === oppBaseStat && l.commence_time === opp.commence_time
    })
    if (correlated) {
      setMultiError({ key, msg: `⚠ Correlated: ${oppPlayer} already has a ${oppBaseStat.replace('player_', '').replace(/_/g, ' ')} leg` })
      setTimeout(() => setMultiError(null), 2800)
      return
    }

    if (multiLegs.length === 0) {
      if (opp.bookmakers.length === 1) {
        addMultiLeg(opp, opp.bookmakers[0].name)
      } else {
        setPickerState({ opp })
      }
      return
    }

    if (!opp.bookmakers.some(b => b.name === lockedBook)) {
      setMultiError({ key, msg: `Not on ${lockedBook}` })
      setTimeout(() => setMultiError(null), 2500)
      return
    }

    addMultiLeg(opp, lockedBook)
  }

  // ─── Fetch ─────────────────────────────────────────────────────────────────

  const fetchOpportunities = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const { data, error } = await supabase.functions.invoke('one-sided-ev-proxy')
      if (error) throw error
      setOpportunities(data.opportunities || [])
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => { fetchOpportunities() }, [])

  useEffect(() => {
    if (!user) return
    getUserFilters(user.id, 'prop_ev').then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({ id: f.id, name: f.filter_name, isDefault: f.is_default || false, ...f.filter_data })))
    })
  }, [user])

  useEffect(() => {
    if (!user || defaultFilterLoaded) return
    getDefaultFilter(user.id, 'prop_ev').then(({ data }) => {
      if (data) applyFilterData(data.filter_data)
      setDefaultFilterLoaded(true)
    })
  }, [user, defaultFilterLoaded])

  useVisiblePolling(() => fetchOpportunities(true), 30000, autoRefreshEnabled && isVisible)

  useEffect(() => {
    const isMarketExcluded = buildMarketExcluder(filters.excludedMarketTypes)

    const filtered = opportunities.filter(opp => {
      if (filters.excludedSports.size > 0 && filters.excludedSports.has(opp.sport || 'AFL')) return false
      if (filters.excludedBookmakers.size > 0 && filters.excludedBookmakers.has(opp.bookmaker)) return false
      if (isMarketExcluded(opp)) return false
      if (opp.original_odds < filters.odds[0] || opp.original_odds > filters.odds[1]) return false
      if (opp.ev_percentage < filters.ev[0] || opp.ev_percentage > filters.ev[1]) return false
      const hours = getHoursUntilGame(opp.commence_time)
      if (hours < filters.hoursUntil[0] || hours > filters.hoursUntil[1]) return false
      if ((opp.bookmaker_count || 1) < filters.minBooks) return false
      return true
    })

    const sorted = [...filtered].sort((a, b) => {
      let av, bv
      if (sortBy === 'ev_percentage') { av = a.ev_percentage; bv = b.ev_percentage }
      else if (sortBy === 'original_odds') { av = a.original_odds;  bv = b.original_odds }
      else if (sortBy === 'kelly_stake')   { av = a.kelly_stake;    bv = b.kelly_stake }
      else if (sortBy === 'commence_time') {
        av = new Date(a.commence_time + 'Z').getTime()
        bv = new Date(b.commence_time + 'Z').getTime()
      }
      else { av = 0; bv = 0 }
      return sortDir === 'desc' ? bv - av : av - bv
    })

    setFilteredOpportunities(sorted)
    setCurrentPage(1)
  }, [opportunities, filters, sortBy, sortDir, kellyFraction, bankroll, roundStakes])

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const applyFilterData = (fd) => {
    setFilters({
      excludedBookmakers:  new Set(fd.filters.excludedBookmakers  || []),
      excludedMarketTypes: new Set(fd.filters.excludedMarketTypes || []),
      excludedSports:      new Set(fd.filters.excludedSports      || []),
      odds:       fd.filters.odds       || DEFAULT_FILTERS.odds,
      ev:         fd.filters.ev         || DEFAULT_FILTERS.ev,
      hoursUntil: fd.filters.hoursUntil || DEFAULT_FILTERS.hoursUntil,
      minBooks:   fd.filters.minBooks   ?? DEFAULT_FILTERS.minBooks,
    })
    if (fd.stakeSettings) {
      setKellyFraction(fd.stakeSettings.kellyFraction)
      setBankroll(fd.stakeSettings.bankroll)
      setRoundStakes(fd.stakeSettings.roundStakes)
    }
  }

  const handleSaveFilter = async (name) => {
    if (!name.trim()) return
    const fd = {
      filters: {
        excludedBookmakers:  [...filters.excludedBookmakers],
        excludedMarketTypes: [...filters.excludedMarketTypes],
        excludedSports:      [...filters.excludedSports],
        odds: filters.odds, ev: filters.ev, hoursUntil: filters.hoursUntil, minBooks: filters.minBooks,
      },
      stakeSettings: { kellyFraction, bankroll, roundStakes },
    }
    const { data } = await saveUserFilter(user.id, 'prop_ev', name.trim(), fd)
    if (data) setSavedFilters(prev => [...prev, { id: data.id, name: data.filter_name, isDefault: false, ...data.filter_data }])
    setFilterName('')
  }

  const handleLoadFilter   = (saved) => { applyFilterData(saved); setActiveFilterId(saved.id) }
  const handleDeleteFilter = async (id) => {
    await deleteUserFilter(id)
    setSavedFilters(prev => prev.filter(f => f.id !== id))
  }
  const handleToggleDefault = async (id) => {
    const current = savedFilters.find(f => f.id === id)
    if (current?.isDefault) {
      await unsetDefaultFilter(user.id, 'prop_ev', id)
    } else {
      await setDefaultFilter(user.id, 'prop_ev', id)
    }
    setSavedFilters(prev => prev.map(f => ({ ...f, isDefault: f.id === id ? !current?.isDefault : false })))
  }

  // ─── Pagination ────────────────────────────────────────────────────────────

  const visibleOpps = groupedOpportunities.filter(o => !isHidden(o))
  const totalPages  = Math.ceil(visibleOpps.length / itemsPerPage)
  const pagedOpps   = isPreview
    ? visibleOpps.slice(0, 4)
    : visibleOpps.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="bcm-container">

      {/* Header */}
      <div className="bcm-header">
        <div className="bcm-action-row">

          {/* Left group: Filters, Multi, Hidden */}
          <div className="bcm-action-left">
            <button className="bc-btn" onClick={() => setShowFilterPanel(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
              </svg>
              Filters
            </button>

            {!isPreview && (
              <button
                className={`bc-btn bc-btn-toggle${multiLegs.length > 0 ? ' toggle-active' : ''}`}
                onClick={() => setShowMultiModal(true)}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                </svg>
                {multiLegs.length > 0 && <span className="bc-multi-count">{multiLegs.length}</span>}
              </button>
            )}

            {Object.keys(hiddenBets).length > 0 && (
              <button className="bc-btn" onClick={() => setShowModal(true)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
                {Object.keys(hiddenBets).length}
              </button>
            )}
          </div>

          {/* Right group: Auto refresh + Manual refresh */}
          <div className="bcm-action-right">
            <button
              className={`bc-btn bc-btn-toggle${autoRefreshEnabled ? ' toggle-active' : ''}`}
              onClick={() => setAutoRefreshEnabled(v => !v)}
            >
              Auto
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
            <button className="bc-btn bc-icon-btn" onClick={() => fetchOpportunities()} disabled={loading}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>

        </div>
      </div>

      {/* Error */}
      {error && <div className="bc-error" style={{ margin: '12px' }}>Error: {error}</div>}

      {/* Loading */}
      {loading && (
        <div className="bc-loading">
          <div className="bc-spinner" />
        </div>
      )}

      {/* Cards */}
      {!loading && (
        <>
        {!noteDismissed && (
          <div className="bc-propev-note">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>
              <strong>For research only.</strong> Diff is calculated from over odds only, so it isn't as precise as the regular EV page. Use this tool to spot outliers, then do your own research to find the profitable bets. <strong>New here?</strong> We recommend starting with the{' '}
              <button className="bc-propev-note-link bc-propev-note-inline" onClick={() => window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'ev' }))}>EV page</button>. The work is already done there, so you can simply follow the selections.
              {onOpenInfo && <button className="bc-propev-note-link" onClick={onOpenInfo}>More info</button>}
            </span>
            <button className="bc-propev-note-close" onClick={dismissNote} aria-label="Dismiss">✕</button>
          </div>
        )}
        <div className="bcm-cards">
          {pagedOpps.map((opp, idx) => {
            const blurCard  = isPreview && idx > 0
            const blurStyle = blurCard ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}
            const inMulti  = isInMulti(opp)
            const legIdx   = multiLegs.findIndex(l => l._key === multiLegKey(opp))
            const cardKey  = multiLegKey(opp)
            const isExpanded = expandedCard === cardKey

            const bookEntries = Object.entries(opp.all_bookmaker_odds || {})
              .sort((a, b) => b[1].odds - a[1].odds)
            const hasUnder    = bookEntries.some(([, d]) => d.under_odds)
            const topOver     = bookEntries[0]?.[1].odds
            const bestOverSet = new Set(bookEntries.filter(([, d]) => d.odds === topOver).map(([b]) => b))
            const topUnder    = hasUnder ? Math.max(...bookEntries.map(([, d]) => d.under_odds || 0)) : null
            const bestUnderSet = hasUnder
              ? new Set(bookEntries.filter(([, d]) => d.under_odds === topUnder).map(([b]) => b))
              : new Set()

            return (
              <React.Fragment key={idx}>
              <PropEvCard
                opp={opp}
                diffClass={evBadgeClass(opp)}
                oddsText={convertOdds(opp.original_odds, oddsFormat)}
                stakeText={computeStake(opp.kelly_stake)}
                style={blurStyle}
                blurPick={isPreview && idx === 0}
                inMulti={inMulti}
                legIdx={legIdx}
                isExpanded={isExpanded}
                tracked={isTracked(opp)}
                showButtons={!isPreview}
                onOpenBook={(b) => !isPreview && b.game_url && window.open(b.game_url, '_blank')}
                onMulti={() => handleMultiLegClick(opp)}
                onExpand={() => setExpandedCard(isExpanded ? null : cardKey)}
                onTrack={() => !isTracked(opp) && openTrackModal(opp)}
                onShare={() => setShareBet(opp)}
                onHide={() => hideOpp(opp)}
              >
                {/* Odds dropdown */}
                {isExpanded && (
                  <div className="bcm-odds-dropdown">
                    <div className="bcm-odds-header">
                      <span className="bcm-odds-label over">Over</span>
                      <span />
                      <span className="bcm-odds-label under">Under</span>
                    </div>
                    {bookEntries.map(([bName, data]) => {
                      const isBestOver  = bestOverSet.has(bName)
                      const isBestUnder = bestUnderSet.has(bName)
                      const isBestBook  = isBestOver || isBestUnder
                      return (
                        <div key={bName} className={`bcm-odds-row${isBestBook ? ' best-row' : ''}`}>
                          <span className={`bcm-odds-val${isBestOver ? ' is-best' : ''}`}>
                            {convertOdds(data.odds, oddsFormat)}
                          </span>
                          {BOOKMAKER_LOGOS[bName]
                            ? <img
                                src={BOOKMAKER_LOGOS[bName]}
                                alt={bName}
                                className={`bcm-odds-logo${isBestBook ? ' is-best-book' : ''}`}
                              />
                            : <span className={`bcm-odds-bname${isBestBook ? ' is-best-book' : ''}`}>{bName}</span>
                          }
                          <span className={`bcm-odds-val${isBestUnder ? ' is-best' : ''}`}>
                            {data.under_odds ? convertOdds(data.under_odds, oddsFormat) : '—'}
                          </span>
                        </div>
                      )
                    })}

                    {/* Stats */}
                    <div className="bcm-stats-grid">
                      {typeof opp.fair_odds === 'number' && (
                        <div className="bcm-stat">
                          <span className="bcm-stat-label bc-stat-label-info">
                            Market Avg
                            <button className="bc-stat-info-btn" onClick={e => { e.stopPropagation(); setShowAvgInfo(v => !v) }} aria-label="What is Market Avg?">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                            </button>
                          </span>
                          <span className="bcm-stat-val">{opp.fair_odds.toFixed(2)}</span>
                        </div>
                      )}
                      {opp.last_update && (
                        <div className="bcm-stat">
                          <span className="bcm-stat-label">Updated</span>
                          <span className="bcm-stat-val">{formatTimeWithSeconds(opp.last_update)}</span>
                        </div>
                      )}
                    </div>
                    {showAvgInfo && typeof opp.fair_odds === 'number' && (
                      <p className="bc-stat-info-text">{MARKET_AVG_TIP}</p>
                    )}
                  </div>
                )}

                {/* Correlated / bookmaker error tip */}
                {multiError?.key === multiLegKey(opp) && (
                  <div className="bcm-err-tip">{multiError.msg}</div>
                )}
              </PropEvCard>

              {/* Preview wall — right below the first (teaser) card */}
              {isPreview && idx === 0 && (
                <PreviewWall
              title={groupedOpportunities.length > 0
                ? `${groupedOpportunities.length} player prop opportunities right now`
                : 'Player prop opportunities right now'}
              features={['Real time odds from all major bookmakers', 'One sided EV calculations', 'Automatic multi builder', 'Much more...']}
              planName="Sports Premium"
              accent="#60a5fa"
              hadTrial={hadTrial}
            />
              )}
              </React.Fragment>
            )
          })}

          {/* Preview wall fallback — still shows when there are zero opportunities */}
          {isPreview && pagedOpps.length === 0 && (
            <PreviewWall
              title={groupedOpportunities.length > 0
                ? `${groupedOpportunities.length} player prop opportunities right now`
                : 'Player prop opportunities right now'}
              features={['Real time odds from all major bookmakers', 'One sided EV calculations', 'Automatic multi builder', 'Much more...']}
              planName="Sports Premium"
              accent="#60a5fa"
              hadTrial={hadTrial}
            />
          )}

          {/* Empty */}
          {!isPreview && visibleOpps.length === 0 && (
            <div className="bc-empty">No opportunities match your filters.</div>
          )}
        </div>
        </>
      )}

      {/* Pagination */}
      {!isPreview && !loading && totalPages > 1 && (
        <div className="bcm-pagination">
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === 1}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === 1}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="bc-page-info">{currentPage} / {totalPages}</span>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === totalPages}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === totalPages}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
          </button>
        </div>
      )}

      {/* Floating multi FAB — appears once user has selected legs */}
      {!isPreview && multiLegs.length > 0 && !showMultiModal && (
        <button
          className="bc-multi-fab bc-multi-fab-mobile"
          onClick={() => setShowMultiModal(true)}
          title="Open Multi Builder"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          <span className="bc-multi-fab-label">Multi</span>
          <span className="bc-multi-fab-count">{multiLegs.length}</span>
        </button>
      )}

      {/* Modals — all reused from desktop */}
      <HiddenBetsModal
        mobile
        isOpen={showModal}
        hiddenBets={hiddenBets}
        onUnhide={unhideOpp}
        onUnhideAll={clearAll}
        onClose={() => setShowModal(false)}
      />

      <TrackBetModal
        isOpen={showTrackModal}
        opp={trackingOpp}
        form={trackForm}
        onFormChange={setTrackForm}
        onConfirm={() => saveTrack(false)}
        onConfirmAndHide={() => saveTrack(true)}
        onClose={closeTrackModal}
      />

      {/* Share Modal */}
      {shareBet && <ShareModal bet={shareBet} showMarket={false} onClose={() => setShareBet(null)} />}

      {/* Bookmaker picker — fixed/centered on mobile */}
      {pickerState && (
        <>
          <div className="bc-picker-backdrop" onClick={() => setPickerState(null)} />
          <div className="bcm-picker">
            <div className="bc-picker-title">Select Bookmaker</div>
            {pickerState.opp.bookmakers.map(b => (
              <button
                key={b.name}
                className="bc-picker-option"
                onClick={() => { addMultiLeg(pickerState.opp, b.name); setPickerState(null) }}
              >
                {BOOKMAKER_LOGOS[b.name]
                  ? <img src={BOOKMAKER_LOGOS[b.name]} alt={b.name} className="bc-picker-logo" />
                  : <span style={{ width: 26 }} />}
                <span className="bc-picker-name">{b.name}</span>
              </button>
            ))}
          </div>
        </>
      )}

      <PropEvMultiBuilder
        isOpen={showMultiModal}
        onClose={() => setShowMultiModal(false)}
        rawOpportunities={opportunities}
        selectedLegs={multiLegs}
        onLegsChange={setMultiLegs}
        kellyFraction={kellyFraction}
        bankroll={bankroll}
        roundStakes={roundStakes}
        mobile
      />

      <FilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={filters}
        setFilters={setFilters}
        kellyFraction={kellyFraction}
        setKellyFraction={setKellyFraction}
        bankroll={bankroll}
        setBankroll={setBankroll}
        roundStakes={roundStakes}
        setRoundStakes={setRoundStakes}
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        filterName={filterName}
        setFilterName={setFilterName}
        onSaveFilter={handleSaveFilter}
        onLoadFilter={handleLoadFilter}
        onDeleteFilter={handleDeleteFilter}
        onToggleDefault={handleToggleDefault}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortDir={sortDir}
        setSortDir={setSortDir}
      />

      <PropEvTour isVisible={isVisible} isMobile />

    </div>
  )
}
